import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Card, Form, InputGroup, Spinner, Table } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import { adminApi, extractError } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

const STATUS_VARIANT = {
  draft: 'secondary',
  in_review: 'info',
  reviewed: 'primary',
  published: 'success',
  archived: 'dark',
};

export default function MyCandidatesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');

  async function refresh() {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page: 1, page_size: 100 });
      if (q) params.set('q', q);
      const res = await adminApi.get(`/admin/candidates?${params.toString()}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(() => { refresh(); }, q ? 200 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const displayName = (user?.first_name || user?.full_name || user?.email || '').split(' ')[0];

  return (
    <>
      <PageHeader
        title={`Welcome${displayName ? `, ${displayName}` : ''}`}
        subtitle="These are the candidate profiles assigned to you. Click any candidate to view or edit their profile."
      />
      <div className="page-content">
        {error && <Alert variant="danger">{error}</Alert>}

        <Card className="mb-3">
          <Card.Body>
            <Form.Label className="text-muted" style={{ fontSize: '0.78rem' }}>Search</Form.Label>
            <InputGroup>
              <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
              <Form.Control
                placeholder="Filter by name, slug, or office"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="my-candidates-search-input"
              />
            </InputGroup>
          </Card.Body>
        </Card>

        <Card data-testid="my-candidates-card">
          {loading ? (
            <div className="text-center p-5"><Spinner animation="border" /></div>
          ) : items.length === 0 ? (
            <div className="empty-state" data-testid="my-candidates-empty">
              <i className="bi bi-person-badge" />
              <div>You don&apos;t have any candidates assigned yet.</div>
              <small className="text-muted">Once an administrator assigns you to a candidate, they&apos;ll appear here.</small>
            </div>
          ) : (
            <Table hover responsive size="sm" className="mb-0" data-testid="my-candidates-table">
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Status</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} data-testid={`my-candidate-row-${c.id}`}>
                    <td>
                      <Link to={`/candidates/${c.id}`} className="text-decoration-none fw-semibold" data-testid={`my-candidate-link-${c.id}`}>
                        {c.full_name}
                      </Link>
                      <div className="text-muted" style={{ fontSize: '0.78rem' }}>{c.slug}</div>
                    </td>
                    <td>
                      <Badge bg={STATUS_VARIANT[c.status] || 'secondary'}>{c.status}</Badge>
                    </td>
                    <td className="text-muted" style={{ fontSize: '0.85rem' }}>{c.updated_at?.slice(0, 10) || ''}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
        {!loading && items.length > 0 && (
          <div className="text-muted mt-2" style={{ fontSize: '0.85rem' }}>
            {total} candidate{total === 1 ? '' : 's'} assigned to you.
          </div>
        )}
      </div>
    </>
  );
}
